import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { css } from '@compiled/react';
import Textfield from '@atlaskit/textfield';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import { DatePickerCalendar } from './DatePickerCalendar';

const MONTH_ABBREVS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Parse display format "Mar 5, 2026" for calendar view
function parseDisplayDate(value: string): Date | null {
  if (!value.trim()) return null;
  const trimmed = value.trim();
  for (let month = 0; month < 12; month++) {
    const prefix = MONTH_ABBREVS[month];
    if (trimmed.startsWith(prefix + ' ')) {
      const rest = trimmed.slice(prefix.length + 1);
      const match = rest.match(/^(\d{1,2}),\s*(\d{4})$/);
      if (match) {
        const day = parseInt(match[1], 10);
        const year = parseInt(match[2], 10);
        if (day >= 1 && day <= 31) {
          const d = new Date(year, month, day);
          if (d.getMonth() === month && d.getDate() === day) return d;
        }
      }
    }
  }
  return null;
}

// Display format: "Mar 5, 2026" (3-char month, first capital, day, year)
function formatDisplayDate(date: Date): string {
  return `${MONTH_ABBREVS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

type Props = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  isInvalid?: boolean;
};

export const BirthDateField: React.FC<Props> = ({ id, value, onChange, isInvalid }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const parsed = parseDisplayDate(value);
    const today = new Date();
    if (parsed) return { month: parsed.getMonth(), year: parsed.getFullYear() };
    return { month: today.getMonth(), year: today.getFullYear() };
  });

  const parsedValue = useMemo(() => parseDisplayDate(value), [value]);

  const handleViewChange = useCallback((month: number, year: number) => {
    setViewMonth({ month, year });
  }, []);

  const handleSelect = useCallback(
    (date: Date) => {
      onChange(formatDisplayDate(date));
      setIsOpen(false);
    },
    [onChange]
  );

  const handleOpen = useCallback(() => {
    const parsed = parseDisplayDate(value);
    if (parsed) {
      setViewMonth({ month: parsed.getMonth(), year: parsed.getFullYear() });
    }
    setIsOpen(true);
  }, [value]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Click outside to close — no Portal, so host won't set aria-hidden
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  return (
    <div ref={wrapperRef} css={wrapperStyles}>
      <div css={inputWrapperStyles} onClick={handleOpen}>
        <Textfield
          id={id}
          placeholder="MM/DD/YYYY"
          value={value}
          isReadOnly
          isCompact
          isInvalid={isInvalid}
          elemAfterInput={
            <button
              type="button"
              css={triggerButtonStyles}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
              title="Select a date for Birth date"
              aria-label="Select a date for Birth date"
            >
              <CalendarIcon label="" size="small" />
            </button>
          }
        />
      </div>
      <div css={[dropdownStyles, !isOpen && hiddenStyles]}>
        <DatePickerCalendar
          value={parsedValue}
          viewMonth={viewMonth.month}
          viewYear={viewMonth.year}
          onViewChange={handleViewChange}
          onSelect={handleSelect}
        />
      </div>
    </div>
  );
};

const wrapperStyles = css({
  position: 'relative',
  width: '100%',
});

const inputWrapperStyles = css({
  width: '100%',
  cursor: 'pointer',
});

const dropdownStyles = css({
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: '0.5rem',
  zIndex: 500,
  backgroundColor: '#fff',
  borderRadius: '0.25rem',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  border: '1px solid #dfe1e6',
});

const hiddenStyles = css({
  display: 'none',
});

const triggerButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 0.75rem',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'rgb(100, 100, 100)',
  ':hover': {
    color: '#42526e',
  },
});
